import { doc, getDoc, setDoc, deleteDoc } from 'firebase/firestore'
import { db } from '@/firebase'
import { RULE_EXPECTATIONS, attempt, accessDetail, fmtExpected } from '../ruleExpectations.js'

// DESTRUCTIVE (dev project only): user-document lifecycle through live rules —
// create (governance policy + tier checks), read, update a non-privileged field
// on a NON-owned doc (admin-tier path), delete (super-admin tier). Uses a fake
// uid prefixed smoke_ so no real Auth account is involved; syncUserClaims logs
// "user not found" and does nothing, the expected no-op for a doc without an
// Auth user.
//
// Role-aware: each step asserts the server's answer against the rules mirror, so
// a correct denial for a non-admin role counts as a pass. If the role cannot
// create users, the later steps are skipped (no doc to act on).
export default {
  id: 'usersCrud',
  label: 'User create/read/update/delete',
  description: 'Staff user document lifecycle asserted against the rules for your role.',
  destructive: true,

  async run(ctx, step) {
    const role = ctx.roleClaim
    const ref = doc(db, 'users', `smoke_user_${ctx.runId.slice(-8)}`)

    const expectCreate = RULE_EXPECTATIONS.userCreate(role, { governance: ctx.userGovernance })
    const expectRead = RULE_EXPECTATIONS.userRead(role)
    const expectUpdate = RULE_EXPECTATIONS.userUpdate(role)
    const expectDelete = RULE_EXPECTATIONS.userDelete(role)

    let created = false
    try {
      await step(`create user doc (expected: ${fmtExpected(expectCreate)})`, async () => {
        const { allowed } = await attempt(() =>
          setDoc(ref, {
            name: 'SMOKE Test User',
            email: 'smoke-user@example.invalid',
            role: 'teacher',
            status: 'active',
            ...(ctx.schoolId
              ? { schoolIds: [ctx.schoolId], schoolId: ctx.schoolId, primarySchoolId: ctx.schoolId }
              : {}),
            __smokeTest: true,
            __smokeRunId: ctx.runId,
            createdAt: new Date().toISOString()
          })
        )
        created = allowed
        return allowed
          ? `${accessDetail(expectCreate, allowed)} — users/${ref.id}`
          : accessDetail(expectCreate, allowed)
      })

      if (!created) {
        step.skip('remaining lifecycle', 'Role cannot create — later steps skipped')
        return
      }

      await step(`read user back (expected: ${fmtExpected(expectRead)})`, async () => {
        const { allowed } = await attempt(async () => {
          const snap = await getDoc(ref)
          if (!snap.exists()) throw new Error('Created user doc not found on read-back')
        })
        return accessDetail(expectRead, allowed)
      })

      await step(
        `update user, non-privileged field (expected: ${fmtExpected(expectUpdate)})`,
        async () => {
          const { allowed } = await attempt(() =>
            setDoc(ref, { name: 'SMOKE Test User (renamed)' }, { merge: true })
          )
          return accessDetail(expectUpdate, allowed)
        }
      )

      await step(`delete user (expected: ${fmtExpected(expectDelete)})`, async () => {
        const { allowed } = await attempt(() => deleteDoc(ref))
        return accessDetail(expectDelete, allowed)
      })
    } finally {
      await deleteDoc(ref).catch(() => {})
    }
  }
}
